import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Translate } from 'react-localize-redux';
import CategoryFeedback, { CategoryFeedbackValue } from './CategoryFeedback';
import { QcOutcome } from './imageMeta';
import { QcCategoriesState } from './qcCategories';

/** Everything a reviewer can say about one photograph. */
export type ImageAssessmentValue = CategoryFeedbackValue & {
    outcome: QcOutcome | null;
};

interface ImageAssessmentProps {
    /**
     * The categories for THIS image's eCRF field — the universal section plus the one for
     * its shot type — and where that fetch has got to.
     */
    categories: QcCategoriesState;
    value: ImageAssessmentValue;
    onChange: (next: ImageAssessmentValue) => void;
    /** The visit is approved (LOCKED): render the verdict and feedback as static text. */
    readOnly?: boolean;
}

/**
 * SKIN-241 — the verdict on one photograph, and the feedback attached to it.
 *
 * SKIN-784 changed the layout, not the rules: every category is on screen from the start, with
 * Correct under them. Ticking a category is answering "needs feedback"; pressing Correct is
 * answering "correct" and clears whatever was ticked. Until one of the two happens the photograph
 * is not reviewed — as before, the outcome is always the reviewer's explicit answer.
 *
 * Controlled: it holds no state and emits a whole `ImageAssessmentValue` on every change,
 * because the value is upserted whole — the API replaces categories rather than merging
 * them, and an absent comment clears the stored note. A component keeping half the value
 * locally would let the two drift.
 */
export default function ImageAssessment({
    categories,
    value,
    onChange,
    readOnly = false,
}: ImageAssessmentProps) {
    /**
     * Feedback exists under `needs` and nowhere else. Correct therefore clears it rather than
     * hiding it: the API refuses `correct` with categories or a note (400), and it would be two
     * contradictory statements about one photograph regardless.
     */
    const markCorrect = () => onChange({ outcome: 'correct', categoryIds: [], comment: '' });

    return (
        <Box sx={{ mt: 1.5 }} data-testid="image-assessment" data-read-only={readOnly ? 'true' : undefined}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                <Translate id="hospital.quality-check.review.assessment" />
            </Typography>

            {readOnly ? (
                <React.Fragment>
                    {/*
                      * LOCKED is read-only for everyone including the reviewer who
                      * approved, so this is not a disabled form — there is nothing to
                      * disable. Disabled inputs would suggest the visit could be reopened
                      * from here, which it cannot.
                      */}
                    <Typography variant="body2" data-testid="outcome-static" data-outcome={value.outcome ?? 'unassessed'}>
                        <Translate
                            id={value.outcome === 'correct'
                                ? 'hospital.quality-check.review.correct'
                                : value.outcome === 'needs'
                                    ? 'hospital.quality-check.review.needs-feedback'
                                    : 'hospital.quality-check.review.unassessed'}
                        />
                    </Typography>
                    <CategoryFeedback categories={categories} value={value} onChange={() => undefined} readOnly />
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="assessment-instructions">
                        <Translate id="hospital.quality-check.review.tick-or-correct" />
                    </Typography>
                    <CategoryFeedback
                        categories={categories}
                        value={value}
                        // Any tick or note is the answer "needs feedback". Unticking the last one
                        // leaves it there, as before; only Correct says the photograph is right.
                        onChange={(next) => onChange({ ...next, outcome: 'needs' })}
                        showSectionHeadings
                        // Names the reader: this note goes to the dermatologist who took the
                        // photograph, not to another reviewer.
                        notePlaceholderId="hospital.quality-check.review.note-placeholder"
                    />
                    <Button
                        fullWidth
                        color="success"
                        variant={value.outcome === 'correct' ? 'contained' : 'outlined'}
                        startIcon={<CheckIcon />}
                        onClick={markCorrect}
                        aria-pressed={value.outcome === 'correct'}
                        data-testid="outcome-correct"
                        sx={{ mt: 2 }}
                    >
                        <Translate id="hospital.quality-check.review.correct" />
                    </Button>
                </React.Fragment>
            )}
        </Box>
    );
}
